import { Box, Skeleton, Stack, Typography, alpha, Chip, Avatar } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LaunchIcon from '@mui/icons-material/Launch';
import { Link as RouterLink } from 'react-router-dom';
import { CRM_PALETTE as P } from '../../../theme/brandPalette';
import { useGetCustomerCardQuery } from '../../../store/api/customersApi';

const STATUS_COLOR: Record<string, string> = {
  active:    '#34D399',
  closed:    '#60A5FA',
  discarded: '#64748B',
};

const STATUS_LABEL: Record<string, string> = {
  active:    'Activo',
  closed:    'Cerrado',
  discarded: 'Descartado',
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
}

interface Props {
  /** Mongo _id del cliente */
  customerId: string;
}

export function CustomerInlineCard({ customerId }: Props) {
  const golden = P.brand.golden;
  const { data, isLoading, isError } = useGetCustomerCardQuery(customerId);

  if (isLoading) {
    return (
      <Box
        sx={{
          my: 1, p: 1.5,
          border: `1px solid ${alpha(golden, 0.2)}`,
          borderRadius: 1.5,
          bgcolor: alpha('#141414', 0.5),
          minWidth: 'min(260px, 100%)',
          maxWidth: 340,
        }}
      >
        <Stack direction="row" spacing={1}>
          <Skeleton variant="circular" width={40} height={40} />
          <Box sx={{ flex: 1 }}>
            <Skeleton width="70%" />
            <Skeleton width="50%" />
          </Box>
        </Stack>
      </Box>
    );
  }

  if (isError || !data) {
    // Fallback: link simple para no perder la referencia si la API falla
    return (
      <RouterLink to={`/customers/${customerId}`} style={{ color: golden, fontWeight: 600 }}>
        Ver cliente
      </RouterLink>
    );
  }

  return (
    <Box
      component={RouterLink}
      to={`/customers/${data._id}`}
      sx={{
        display: 'block',
        textDecoration: 'none',
        my: 1, p: 1.5,
        border: `1px solid ${alpha(golden, 0.25)}`,
        borderRadius: 1.5,
        bgcolor: alpha('#141414', 0.7),
        minWidth: 'min(260px, 100%)',
        maxWidth: 340,
        transition: 'all 0.15s',
        '&:hover': {
          borderColor: golden,
          bgcolor: alpha(golden, 0.06),
          transform: 'translateY(-1px)',
        },
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Avatar
          sx={{
            width: 40, height: 40, flexShrink: 0,
            bgcolor: alpha(golden, 0.15), color: '#fff',
            fontSize: 14, fontWeight: 700,
          }}
        >
          {initials(data.name)}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.5} mb={0.25}>
            <Typography
              noWrap
              sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary', flex: 1, minWidth: 0 }}
            >
              {data.name}
            </Typography>
            <Chip
              label={STATUS_LABEL[data.status] ?? data.status}
              size="small"
              sx={{
                height: 14, fontSize: 9, fontWeight: 700,
                bgcolor: alpha(STATUS_COLOR[data.status] ?? '#888', 0.2),
                color: STATUS_COLOR[data.status] ?? '#888',
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
            <LaunchIcon sx={{ fontSize: 11, color: alpha(golden, 0.5) }} />
          </Stack>

          {data.email && (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary', mb: 0.25 }}>
              <EmailIcon sx={{ fontSize: 12 }} />
              <Typography noWrap variant="caption" sx={{ fontSize: 11 }}>{data.email}</Typography>
            </Stack>
          )}

          {data.phone && (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary', mb: 0.25 }}>
              <PhoneIcon sx={{ fontSize: 12 }} />
              <Typography noWrap variant="caption" sx={{ fontSize: 11 }}>{data.phone}</Typography>
            </Stack>
          )}

          {data.tags.length > 0 && (
            <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap" useFlexGap>
              {data.tags.slice(0, 3).map((t) => (
                <Chip
                  key={t}
                  label={t}
                  size="small"
                  sx={{
                    height: 15, fontSize: 9,
                    bgcolor: alpha(golden, 0.12), color: '#fff',
                    '& .MuiChip-label': { px: 0.6 },
                  }}
                />
              ))}
            </Stack>
          )}
        </Box>
      </Stack>
    </Box>
  );
}
